import {MapLayer} from "@esWebAPI/es/map/layer/MapLayer";
import {ILayerView, LayerInfo, LayerUpdateMetadata} from "@esWebAPI/es/model/Layer";
import {ColorThematic, SymbolThematic} from "@esWebAPI/es/model/Thematic";
import {
    type DataDrivenPropertyValueSpecification,
    ExpressionFilterSpecification
} from "@maplibre/maplibre-gl-style-spec";
import {data, Map, source} from "azure-maps-control";
import {DatasourceManager} from "@esWebAPI/es/map/module/DatasourceManager";
import {IEventChannel, SchedulePointStyleSettings} from "@esWebAPI/es/model/Models";
import {Service} from "@esWebAPI/es/model/Schedule";
import {Feature} from "@esWebAPI/es/model/Feature";
import {InCriteria} from "@esWebAPI/es/model/Criteria";
import {IRoutingModule} from "@esWebAPI/es/map/module/ScheduleModule";
import {AzureStopSymbolLayer} from "@esWebAPI/es/map/layer/azure/AzureStopSymbolLayer";
import {AzureLabelLayer} from "@esWebAPI/es/map/layer/azure/AzureLabelLayer";
import {ThematicStylerFactory} from "@esWebAPI/es/map/styler/ThematicStylerFactory";
import {AzureImageSpriteManager} from "@esWebAPI/es/map/layer/AzureImageSpriteManager";
import _ from "lodash";
import {isLabelBoxEnabled, isMouseoverLabellingEnabled} from "@esWebAPI/es/map/layer/azure/AzureLayer";
import {AzureMouseoverLabelHandler} from "@esWebAPI/es/map/handler/AzureMouseoverLabelHandler";
import {ImageSpriteGenerator} from "@esWebAPI/es/map/layer/ImageSpriteGenerator";


export class StopSymbolLayer extends MapLayer {

    private readonly spriteManager: AzureImageSpriteManager;
    private readonly stylerFactory: ThematicStylerFactory;
    private readonly schedule: IRoutingModule;
    private readonly spriteGenerator: ImageSpriteGenerator;

    private datasource: source.DataSource;
    private _stopSymbolLayer: AzureStopSymbolLayer|undefined;
    private _labelLayer: AzureLabelLayer|undefined;

    private readonly symbolMapping: {[featureId: number]: string} = {};

    constructor(map: Map, layerView: ILayerView, layerInfo: LayerInfo,
                datasourceManager: DatasourceManager,
                spriteManager: AzureImageSpriteManager,
                stylerFactory: ThematicStylerFactory,
                schedule: IRoutingModule,
                eventChannel: IEventChannel) {
        super(map, layerView, layerInfo, datasourceManager, eventChannel);
        this.spriteManager = spriteManager;
        this.stylerFactory = stylerFactory;
        this.schedule = schedule;

        this.spriteGenerator = new ImageSpriteGenerator(spriteManager);

        this.datasource = this.datasourceManager.createBlank(this.layerView, this.layerInfo, "stop-symbol-source_"+ this.layerView.id);

        this.createLayers().then(() => this.onReady.resolve());
    }

    private async createLayers() {

        await this.updateData();

        this._stopSymbolLayer = new AzureStopSymbolLayer(this.map, this.layerView, this.symbolMapping);
        await this._stopSymbolLayer.create(this.datasource);

        this._labelLayer = new AzureLabelLayer(this.map, this.layerView, this.layerInfo, this.datasourceManager, this.spriteManager, this.stylerFactory, undefined);
        await this._labelLayer.create(this.datasource);

        if (this.layerView.styleSettings.labelSettings.enabled) {
            this._labelLayer.setOptions({textOptions: {offset: this.getLabelOffset()}});
        }

        if (isMouseoverLabellingEnabled(this.layerView)){
            this._mouseoverLabelHandler = new AzureMouseoverLabelHandler(this.map, this);
        }
    }

    private getLabelOffset(): DataDrivenPropertyValueSpecification<any> {

        const basicText = this.layerView.styleSettings.defaultStyle.text;

        if (basicText.position === "CENTRAL") {
            return [0, 0];
        }

        const startEndFeatureIds = _.uniq(this.schedule.getServicesObjRef()
            .filter((s: Service) => s.stopInfo && ["START", "END", "START_END"].includes(s.stopInfo.type))
            .map((s: Service) => s.featureId));

        // Radius in em is symbol size / 2 / text font size

        const boxPadding = isLabelBoxEnabled(this.layerView) ? 10 : 0;
        const textHeight = basicText.height;


        const multiplier = basicText.position === "BELOW" ? 1 : -1; // Negative offset for ABOVE
        return [
            "case",
            ["in", ["id"], ["literal", startEndFeatureIds]],
            ["literal", [0, (48+boxPadding) / 2 / textHeight * multiplier]],
            ["literal", [0, (25+boxPadding) / 2 / textHeight * multiplier]]
        ];
    }

    private async updateData() {
        const services: Service[] = _.uniqBy(
            this.schedule.getServicesObjRef()
                .filter((s: Service) => !!s.address && s.layerViewId === this.layerView.id && !this.schedule.isServiceBreak(s))
                .sort((a: Service, b: Service) => a.featureId - b.featureId),
            (s: Service) => s.featureId);

        if (services.length === 0) {
            this.datasource.clear();
            return;
        }

        const serviceFeatureIds = services.map(s => s.featureId);
        const requiredAttributes = this.datasourceManager.getRequiredAttributes(this.layerView);
        const criteria: InCriteria<number> = {$type: "In", attr: "ES_ID", values: serviceFeatureIds};
        const geoJsonResponse = await es.api.feature.listGeoJson(this.layerView.id, requiredAttributes, criteria);

        const styler = await es.map.vector.style.buildStyler(this.layerView);

        for (const geoJsonFeature of geoJsonResponse.features) {
            const service = services.find(s => s.featureId === geoJsonFeature.id);
            if (service) {
                const symbol = await this.createServiceSymbol(service, geoJsonFeature, styler);
                this.symbolMapping[service.featureId] = symbol;
            }
        }

        // load all the geojson feats directly
        this.datasource.clear();
        this.datasource.add(new data.FeatureCollection(geoJsonResponse.features));
    }

    /**
     * Get the symbol metadata for a service/feature
     * @param {Service} service Service
     * @param {Feature} feature Feature associated to the service
     * @param styler
     * @return {Symbol} Symbol metadata
     */
    private async createServiceSymbol(service: Service, feature: Feature, styler: any): Promise<string> {

        const badgeSymbolId = 57;
        const badgeDefaultSize = 25;

        const services = this.schedule.util.getServicesFromFeat({layerViewId: this.layerView.id, id: service.featureId});
        if (services.length) {

            let stopInfo = services[0].stopInfo;

            if (services.length > 1) {
                const activeServices = services.filter((s:any) => {
                    const shift = this.schedule.util.getShiftFromService(s);
                    return shift && this.schedule.isShiftActive(shift.shiftId);
                });

                const uniques = _.uniq(activeServices.map((s:any) => s?.stopInfo?.type));
                if (uniques.length === 2 && uniques.includes("START") && uniques.includes("END")) {
                    stopInfo = {type: "START_END", stopNo: "", shiftId: activeServices[0].stopInfo.shiftId};
                }
            }

            if (stopInfo && stopInfo.type === "START") {
                return this.spriteManager.addFromStyle({symbolTypeId: 13, size: 48, color: 0});
            }
            if (stopInfo && stopInfo.type === "START_END") {
                return this.spriteManager.addFromStyle({symbolTypeId: 15, size: 48, color: 0});
            }
            if (stopInfo && stopInfo.type === "END") {
                return this.spriteManager.addFromStyle({symbolTypeId: 14, size: 48, color: 0});
            }

            const shift = this.schedule.util.getShiftFromService(services[0]);
            let color = this.schedule.getShortListColorServer();

            if (shift) {
                if ((this.layerView.styleSettings as SchedulePointStyleSettings).useRouteColor) {
                    color = this.schedule.isShiftActive(shift.shiftId) ?
                        this.schedule.getShiftColorServer(shift.shiftId) : this.schedule.getInactiveColorServer();
                } else {
                    const style = es.map.vector.style
                        .getStyle(styler, this.layerView.styleSettings.defaultStyle, feature, this.layerInfo);

                    color = this.schedule.isShiftActive(shift.shiftId) ?
                        style.color : this.schedule.getInactiveColorServer();
                }
            }

            const stopNo = this.schedule.util.getStopNumberFromService(service) ?? 0;
            const text = stopNo > 0 ? String(stopNo) : undefined;

            return this.spriteGenerator.generateRouteBadge({symbolTypeId: badgeSymbolId, size: badgeDefaultSize, color, text});
        }
        return this.spriteManager.addFromStyle({symbolTypeId: badgeSymbolId, size: badgeDefaultSize, color: this.schedule.getShortListColorServer()});
    }

    public async update(_layerUpdateMetadata: LayerUpdateMetadata): Promise<void> {
        await this.updateData();
        this._stopSymbolLayer?.update(this.layerView, this.layerInfo);
        await this._labelLayer?.update(this.layerView, this.layerInfo);

        if (this.layerView.styleSettings.labelSettings.enabled) {
            this._labelLayer?.setOptions({textOptions: {offset: this.getLabelOffset()}});
        }
    }

    protected getColorThematicFilter(_colorThematic?: ColorThematic): ExpressionFilterSpecification | undefined {
        throw undefined;
    }

    protected getSymbolThematicFilter(_symbolThematic?: SymbolThematic): ExpressionFilterSpecification | undefined {
        throw undefined;
    }

    protected hideFeature(featureId: number): void {
        this._stopSymbolLayer?.hideFeature(featureId);
    }

    protected showFeatures(): void {
        this._stopSymbolLayer?.showFeatures();
    }

    public getLayerIds(): string[] {
        const layerIds= [];
        if (this._stopSymbolLayer) {
            layerIds.push(this._stopSymbolLayer.getLayerId());
        }
       return layerIds;
    }

    public destroy(): void {
        super.destroy();
        this._labelLayer?.remove();
        this._stopSymbolLayer?.remove();
        this.datasourceManager.remove(this.datasource.getId());
    }

    public async showOnlyForDay(start: number, end: number) {
        await this.updateData();
        const stopsFeatureIds = this.schedule.getServicesObjRef()
            .filter((s: any) => {
                const serviceStartTime = this.schedule.util.getStartTime(s);
                return s.layerViewId === this.layerView.id && serviceStartTime >= start && serviceStartTime <= end;
            })
            .map((s: any) => s.featureId);
        this._stopSymbolLayer?.setLayerFilter(["in", ['id'], ['literal', stopsFeatureIds]]);
        this._stopSymbolLayer?.update(this.layerView, this.layerInfo);

    }
    public showAllStops() {
        this._stopSymbolLayer?.setLayerFilter(undefined);
        this._stopSymbolLayer?.update(this.layerView, this.layerInfo);
    }

    public getActiveAzureLayerId(): string | undefined {
        return this._stopSymbolLayer?.getLayerId();
    }

    public hide() {
        this._stopSymbolLayer?.hide();
    }
    public show() {
        this._stopSymbolLayer?.show();
    }
}
